import { useState, type FormEvent } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import { Button } from '@/core/ui/Button'
import { Input } from '@/core/ui/Input'
import { useAuth } from '@/features/auth/useAuth'
import { useIsAuthenticated, useSession, useTokenValid } from '@/core/auth/SessionStore'

/**
 * Tela de login com tratamento hero — ícone grande, wordmark, glow radial sutil
 * de fundo. Sente como "ferramenta de campo séria", não como bookmark.
 *
 * Composição:
 *  - Topo (~40% da viewport): área de marca com glow radial e logo
 *  - Centro: card com form (h-12 inputs e botão pra alvo de toque generoso)
 *  - Rodapé: footnote com versão e referência ao SIGAOBRAS
 */
export function LoginRoute() {
  const authed = useIsAuthenticated()
  const tokenValid = useTokenValid()
  const session = useSession()
  const navigate = useNavigate()
  const { login, loading, error } = useAuth()
  const [usuario, setUsuario] = useState('')
  const [senha, setSenha] = useState('')

  // Sessão + token válido → nada a fazer aqui. Sessão com token vencido fica:
  // é o modo "entrar de novo" (os dados locais continuam no aparelho).
  if (authed && tokenValid) return <Navigate to="/" replace />
  const reauth = authed && !tokenValid

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    void login(usuario.trim(), senha)
  }

  return (
    <main className="min-h-svh bg-bg-base flex flex-col relative overflow-hidden">
      {/* Glow radial — ecoa o efeito do icon-source.png. Posicionado mais pra cima
          pra criar foco visual na marca sem competir com o card de form. */}
      <div
        aria-hidden
        className="absolute inset-x-0 top-0 h-[60vh] pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse at top, rgba(77,142,255,0.22) 0%, transparent 60%)',
        }}
      />

      {/* Hero — só o ícone glowing centralizado, sem wordmark embutido */}
      <header className="relative flex flex-col items-center pt-safe-plus-3 px-6 pt-12 pb-8">
        <img
          src="/icon-source.png"
          alt="InfraWork"
          decoding="async"
          className="h-36 w-36"
        />
        <p className="text-on-surface text-base font-medium mt-4 tracking-wide">
          InfraWork
          <span className="text-on-surface-dim"> · Aplicativo de Produção</span>
        </p>
      </header>

      {/* Form */}
      <section className="relative flex-1 flex items-start justify-center px-6">
        <div className="w-full max-w-sm">
          <form
            onSubmit={handleSubmit}
            className="bg-bg-panel rounded-lg border border-border-strong p-6 space-y-5 shadow-xl shadow-black/30"
          >
            {reauth && (
              <div className="rounded-md bg-warn/10 border border-warn/30 px-3 py-2.5 text-xs text-on-surface">
                <span className="font-semibold">Sessão expirada{session?.userNome ? ` · ${session.userNome}` : ''}.</span>{' '}
                Entre de novo para enviar os lançamentos. Nada foi apagado deste aparelho.
              </div>
            )}
            <div className="space-y-2">
              <label
                htmlFor="usuario"
                className="block text-[11px] font-semibold text-on-surface-dim uppercase tracking-[0.08em]"
              >
                Usuário
              </label>
              <Input
                id="usuario"
                type="text"
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                required
                value={usuario}
                onChange={(e) => setUsuario(e.target.value)}
                disabled={loading}
                placeholder="seu.usuario"
                className="h-12"
              />
            </div>

            <div className="space-y-2">
              <label
                htmlFor="senha"
                className="block text-[11px] font-semibold text-on-surface-dim uppercase tracking-[0.08em]"
              >
                Senha
              </label>
              <Input
                id="senha"
                type="password"
                autoComplete="current-password"
                required
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
                disabled={loading}
                placeholder="••••••••"
                className="h-12"
              />
            </div>

            {error && (
              <div
                role="alert"
                className="rounded-md bg-bad/10 border border-bad/30 px-3 py-2.5 text-bad text-sm"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              size="lg"
              variant="primary"
              disabled={loading || !usuario || !senha}
              className="w-full h-12 group"
            >
              <span>{loading ? 'Entrando…' : 'Entrar'}</span>
              {!loading && (
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              )}
            </Button>
            {reauth && (
              <Button type="button" size="lg" variant="ghost" className="w-full h-11" onClick={() => navigate('/', { replace: true })}>
                Continuar sem internet
              </Button>
            )}
          </form>
        </div>
      </section>

      <div className="pb-safe pb-6" />
    </main>
  )
}
